import {
  sanitizePromptAsFilename,
  triggerBrowserFileDownload,
} from '@cartwhl/common/utils';

/** One export slot from GET /motions — URL when ready, otherwise empty. */
export type MotionExportURLSlot = string | undefined;

const EXPORT_DOWNLOAD_STAGGER_MS = 350;

export function parseMotionExportURLSlots(
  raw: (string | null)[] | null | undefined,
): MotionExportURLSlot[] | undefined {
  if (raw == null || raw.length === 0) {
    return undefined;
  }
  return raw.map((u) => (u == null ? undefined : u.trim() || undefined));
}

export function parseMotionExportFilenameSlots(
  raw: (string | null)[] | null | undefined,
): (string | undefined)[] | undefined {
  if (raw == null || raw.length === 0) {
    return undefined;
  }
  return raw.map((name) => (name == null ? undefined : name.trim() || undefined));
}

export function motionUrlPathLooksLikeBvh(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.bvh');
  } catch {
    return url.toLowerCase().includes('.bvh');
  }
}

/** BVH presigned URLs from export slots (for 3D preview playback). */
export function previewBvhUrlsFromExportSlots(
  exportURLs: MotionExportURLSlot[] | undefined,
): string[] {
  if (!exportURLs?.length) {
    return [];
  }
  return exportURLs.filter(
    (u): u is string => typeof u === 'string' && motionUrlPathLooksLikeBvh(u),
  );
}

export function exportSlotsHaveReadyUrl(
  exportURLs: MotionExportURLSlot[] | undefined,
): boolean {
  return Boolean(exportURLs?.some((u) => typeof u === 'string' && Boolean(u)));
}

/** True when the job row has studio/comic BVH URLs or BVH export slots for 3D preview. */
export function batchMotionPreviewHasMotionSource(file: {
  bvhURL?: string;
  bvhURLs?: string[];
  mhrBvhURLs?: string[];
  mhrIdentityJsonURLs?: string[];
  exportURLs?: MotionExportURLSlot[];
}): boolean {
  if (file.bvhURLs?.some((u) => u.trim())) {
    return true;
  }
  if (file.bvhURL?.trim()) {
    return true;
  }
  const mhrBvhs = file.mhrBvhURLs;
  const mhrIds = file.mhrIdentityJsonURLs;
  const mhrAligned =
    !!mhrBvhs?.length &&
    !!mhrIds?.length &&
    mhrBvhs.length === mhrIds.length &&
    mhrBvhs.some((u) => u.trim());
  if (mhrAligned) {
    return true;
  }
  return previewBvhUrlsFromExportSlots(file.exportURLs).length > 0;
}

export function exportSlotIsReady(
  exportURLs: MotionExportURLSlot[] | undefined,
  bodyIndex1: number,
): boolean {
  const url = exportURLs?.[bodyIndex1 - 1];
  return typeof url === 'string' && Boolean(url.trim());
}

export function extensionFromExportUrl(url: string): string | undefined {
  try {
    const base = decodeURIComponent(new URL(url).pathname.split('/').pop() ?? '');
    const match = base.match(/\.([a-z0-9]+)$/i);
    return match?.[1]?.toLowerCase();
  } catch {
    return undefined;
  }
}

export function exportFileExtensionFromType(exportType: string | undefined): string {
  const t = (exportType ?? '').toLowerCase().trim();
  if (t === 'bvh') return 'bvh';
  if (t.startsWith('fbx-')) return 'fbx';
  if (t === 'glb') return 'glb';
  if (t === 'ma') return 'ma';
  if (t === 'mb') return 'mb';
  return 'fbx';
}

function exportBaseNameFromDisplayName(displayName: string): string {
  const trimmed = displayName.trim() || 'motion';
  const noExt = trimmed.replace(/\.[^/.]+$/i, '');
  return sanitizePromptAsFilename(noExt, 120) || 'motion';
}

/** Browser download filename for one export presigned URL. */
export function resolveExportDownloadFilename({
  url,
  displayName,
  bodyIndex1,
  exportType,
  multiBody,
  preferredFilename,
}: {
  url: string;
  displayName: string;
  bodyIndex1?: number;
  exportType?: string;
  multiBody: boolean;
  /** From API `exportFilenames` (matches backend `filenamePrefix`). */
  preferredFilename?: string;
}): string {
  if (preferredFilename?.trim()) {
    return preferredFilename.trim();
  }

  const base = exportBaseNameFromDisplayName(displayName);
  const ext = extensionFromExportUrl(url) ?? exportFileExtensionFromType(exportType);
  if (multiBody && bodyIndex1 != null) {
    const n = String(bodyIndex1).padStart(2, '0');
    return `${base}_${n}.${ext}`;
  }
  return `${base}.${ext}`;
}

/** Browser download filename for the optional camera FBX (`{job_name}_camera.fbx`). */
export function cameraExportFilename(
  displayName: string,
  exportFilenames?: (string | undefined)[],
): string {
  const preferred = exportFilenames?.find((name) => name?.trim())?.trim();
  if (preferred) {
    const withoutExt = preferred.replace(/\.[^/.]+$/i, '');
    // Match actor basenames like `{job}_01` → `{job}_camera.fbx`
    const withCameraSuffix = withoutExt.replace(/_\d+$/, '_camera');
    if (withCameraSuffix !== withoutExt) {
      return `${withCameraSuffix}.fbx`;
    }
    return `${withoutExt}_camera.fbx`;
  }

  const base = exportBaseNameFromDisplayName(displayName);
  return `${base}_camera.fbx`;
}

export async function downloadCameraFbx({
  cameraFbxURL,
  displayName,
  exportFilenames,
}: {
  cameraFbxURL: string;
  displayName: string;
  exportFilenames?: (string | undefined)[];
}): Promise<void> {
  const url = cameraFbxURL.trim();
  if (!url) {
    throw new Error('Camera FBX is not available');
  }
  await triggerBrowserFileDownload(
    url,
    cameraExportFilename(displayName, exportFilenames),
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Downloads presigned export file(s) for a job. When `bodyIndex` is set (1-based),
 * downloads only that slot; otherwise downloads every ready slot sequentially.
 */
export async function downloadMotionExportSlots({
  exportURLs,
  exportFilenames,
  displayName,
  exportType,
  bodyIndex,
}: {
  exportURLs: MotionExportURLSlot[];
  exportFilenames?: (string | undefined)[];
  displayName: string;
  exportType?: string;
  bodyIndex?: number;
}): Promise<void> {
  const multiBody = exportURLs.length > 1;
  const indices: number[] = [];

  if (bodyIndex !== undefined) {
    const idx = bodyIndex - 1;
    if (idx < 0 || idx >= exportURLs.length) {
      throw new Error(`Invalid bodyIndex ${bodyIndex}`);
    }
    if (!exportURLs[idx]) {
      throw new Error('Export file is not ready yet');
    }
    indices.push(idx);
  } else {
    for (let i = 0; i < exportURLs.length; i += 1) {
      if (exportURLs[i]) {
        indices.push(i);
      }
    }
  }

  if (indices.length === 0) {
    throw new Error('No export files are ready to download');
  }

  for (let n = 0; n < indices.length; n += 1) {
    const i = indices[n]!;
    const url = exportURLs[i]!;
    const filename = resolveExportDownloadFilename({
      url,
      displayName,
      bodyIndex1: i + 1,
      exportType,
      multiBody,
      preferredFilename: exportFilenames?.[i],
    });
    await triggerBrowserFileDownload(url, filename);
    if (n < indices.length - 1) {
      await sleep(EXPORT_DOWNLOAD_STAGGER_MS);
    }
  }
}
